import { documentApi, DocumentDownload } from '../services/documentApi';

/**
 * Downloads a document by ID using the same logic as the Angular version
 * @param documentId - The ID of the document to download
 * @param setLoading - Optional function to set loading state
 */
export const downloadDocument = async (
  documentId: string, 
  setLoading?: (loading: boolean) => void
): Promise<void> => {
  try {
    if (setLoading) setLoading(true);

    const documentData = await documentApi.download(documentId);
    handleDocumentDownload(documentData);
  } catch (error) {
    console.error('Error downloading document:', error);
    alert('Failed to download document. Please try again.');
  } finally {
    if (setLoading) setLoading(false);
  }
};

/**
 * Handles the actual download logic based on document type and browser capabilities
 * @param documentData - The document data from the API
 */
export const handleDocumentDownload = (documentData: DocumentDownload): void => {
  const { DocFile, Name, DocumentType } = documentData;

  // Check if it's a base64 data URL
  if (DocFile.includes('base64,')) {
    handleBase64Download(DocFile, Name, DocumentType);
  } else {
    // Direct file URL - create download link
    const anchor = document.createElement('a');
    anchor.href = DocFile;
    anchor.download = Name || 'document';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
};

/**
 * Handles base64 encoded document downloads
 * @param docFile - Base64 data URL
 * @param fileName - Name of the file
 * @param documentType - MIME type of the document
 */
const handleBase64Download = (docFile: string, fileName: string, documentType: string): void => {
  // For PDFs, open in new window (same as Angular version)
  if (documentType === 'application/pdf' || docFile.includes('pdf')) {
    const pdfWindow = window.open('');
    if (pdfWindow) {
      const base64Data = docFile.substr(docFile.indexOf(',') + 1);
      pdfWindow.document.write(
        `<iframe width='100%' height='100%' src='data:application/pdf;base64,${encodeURI(base64Data)}'></iframe>`
      );
    }
    return;
  }

  // For other file types, download the file
  if (isIEBrowser()) {
    handleIEDownload(docFile, fileName, documentType);
  } else {
    // Modern browsers - much easier
    window.open(docFile, '_blank');
  }
};

/**
 * Handles downloads for Internet Explorer (legacy support)
 * @param docFile - Base64 data URL
 * @param fileName - Name of the file
 * @param documentType - MIME type of the document
 */
const handleIEDownload = (docFile: string, fileName: string, documentType: string): void => {
  try {
    const base64Data = docFile.substring(docFile.indexOf('base64,') + 7);
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: documentType });
    
    // IE-specific API
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      // Fallback for other browsers
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error in IE download:', error);
    // Fallback to simple window.open
    window.open(docFile, '_blank');
  }
};

/**
 * Detects if the browser is Internet Explorer
 * @returns true if IE browser
 */
const isIEBrowser = (): boolean => {
  return window.navigator && (window.navigator as any).msSaveOrOpenBlob;
};

/**
 * Gets the appropriate file icon class based on document type (for UI purposes)
 * @param documentType - MIME type of the document
 * @returns Font Awesome icon class
 */
export const getFileIcon = (documentType: string): string => {
  switch (documentType) {
    case 'text/plain':
      return 'fa-file-text-o';
    case 'text/html':
    case 'text/css':
    case 'text/javascript':
    case 'application/xml':
      return 'fa-file-code-o';
    case 'image/jpeg':
    case 'image/png':
    case 'image/gif':
    case 'image/bmp':
    case 'image/webp':
      return 'fa-file-image-o';
    case 'application/pdf':
      return 'fa-file-pdf-o';
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'fa-file-excel-o';
    case 'application/msword':
    case 'application/vnd.ms-word':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'fa-file-word-o';
    case 'application/vnd.ms-powerpoint':
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'fa-file-powerpoint-o';
    default:
      return 'fa-file-o';
  }
}; 